import React from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import Icon from '@mdi/react';
import {
  mdiCash,
  mdiBarcodeScan,
  mdiLanConnect,
  mdiGauge,
  mdiInformationOutline,
  mdiApi,
} from '@mdi/js';
import SeverityEnum from 'features/POSRegisters/interfaces/Severity';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import Tabs from 'features/POSRegisters/POSTable/Registers/interfaces/Tabs';

//#region Constants
export const tabsDictionary: Record<string, Tabs> = {
  SUMMARY: Tabs.SUMMARY,
  IPAD_AIR: Tabs.IPAD_AIR,
  IPAD_MINI: Tabs.IPAD_MINI,
  RECEIPT_PRINTER: Tabs.RECEIPT_PRINTER,
  PAYMENT_TERMINAL: Tabs.PAYMENT_TERMINAL,
};

export enum ModalTab {
  CONNECTIVITY = 'Connectivity',
  PERFORMANCE = 'Performance',
  INFORMATION = 'Information',
  BARCODE_SCANNER = 'Barcode Scanner',
  CASH_DRAWER = 'Cash Drawer',
  API = 'Terminal API',
}
//#endRegion Constants

//#region styles
const StyledTabContainer = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  height: 48px;
  align-items: flex-end;
  padding: 0px 0px 0px 0px;
  background: transparent;
`;

const StyledTab = styled(Button)`
  display: flex;
  padding: 4px 2px;
  color: ${props => props.theme.text};
  text-decoration: none;
  font-family: 'Trade Gothic Bold Condensed';
  font-weight: 700;
  font-size: 14px;
  text-transform: capitalize;
  border-bottom: 4px solid transparent;
  border-radius: 0;
  transition: border-bottom 250ms;

  .MuiButton-startIcon {
    margin-right: 0px;
  }
  .MuiButton-label {
    margin-bottom: 4px;
  }

  &.MuiButton-root {
    line-height: 16px;
    min-width: unset;
  }
  &:hover {
    text-decoration: none;
    color: ${props => props.theme.text};
    border-bottom: 4px solid ${props => props.theme.text};
  }
  &.active {
    color: ${props => props.theme.text};
    border-bottom: 4px solid ${props => props.theme.text};
  }

  &.minor.active {
    border-bottom: 4px solid ${props => props.theme.yellow};
  }

  &.major.active {
    border-bottom: 4px solid ${props => props.theme.orange};
  }

  &.critical.active {
    border-bottom: 4px solid ${props => props.theme.red};
  }

  &.disabled {
    color: ${props => props.theme.disabledStateBlue};
    display: none;
  }

  &.disabled:hover {
    cursor: default;
    text-decoration: none;
    color: ${props => props.theme.disabledStateBlue};
    border-bottom: 4px solid transparent;
  }
`;

const StyledTabText = styled.span`
  display: none;
  margin-left: 8px;
  ${StyledTab}.active & {
    display: inline-block;
  }
`;

const StyledIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  height: 24px;
  width: 24px;
`;

const StyledAlertsNotification = styled.div`
  position: absolute;
  top: -4px;
  left: 12px;
`;
//#end region styles

const getTabIcon = (tab: ModalTab) => {
  switch (tab) {
    case ModalTab.CONNECTIVITY:
      return mdiLanConnect;
    case ModalTab.PERFORMANCE:
      return mdiGauge;
    case ModalTab.INFORMATION:
      return mdiInformationOutline;
    case ModalTab.BARCODE_SCANNER:
      return mdiBarcodeScan;
    case ModalTab.CASH_DRAWER:
      return mdiCash;
    case ModalTab.API:
      return mdiApi;
    default:
      return mdiLanConnect;
  }
};

interface ModalTabs {
  selectedTab: ModalTab;
  handleTabChange: (tab: ModalTab) => void;
  activeTabs: {tab: ModalTab; severity?: SeverityEnum; isValid?: boolean}[];
}

function ModalTabs({selectedTab, activeTabs, handleTabChange}: ModalTabs) {
  const handleClick = (tab: ModalTab, isValid?: boolean) => {
    if (isValid === false) return;
    handleTabChange(tab);
  };

  return (
    <StyledTabContainer>
      {activeTabs.map(({tab, severity, isValid}) => {
        return (
          <StyledTab
            className={
              (tab === selectedTab ? 'active ' : ' ') +
              severity +
              ' ' +
              (isValid === false ? 'disabled' : ' ')
            }
            onClick={() => handleClick(tab, isValid)}
            startIcon={
              <StyledIconContainer>
                <Icon path={getTabIcon(tab)} size={1} />
              </StyledIconContainer>
            }
            key={tab}
          >
            <StyledTabText>{tab}</StyledTabText>
            {severity && (
              <StyledAlertsNotification>
                <SeverityBadge severity={severity} size={0.75} />
              </StyledAlertsNotification>
            )}
          </StyledTab>
        );
      })}
    </StyledTabContainer>
  );
}

export default ModalTabs;
